import { LoaderCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import { normalizarRut, validarRut } from '../../utils/rut'

function ProveedorModal({ proveedor, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(proveedor)

  const [nombre, setNombre] = useState(proveedor?.razon_social || '')
  const [rut, setRut] = useState(proveedor?.rut || '')
  const [activo, setActivo] = useState(proveedor?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !guardando) {
        onClose()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [guardando, onClose])

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarProveedor = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const nombreLimpio = nombre.trim()

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre del proveedor.')
      return
    }

    if (!validarRut(rut)) {
      setError('El RUT ingresado no es válido.')
      return
    }

    const rutNormalizado = normalizarRut(rut)

    try {
      setGuardando(true)

      let proveedorGuardado = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('proveedores')
          .update({
            razon_social: nombreLimpio,
            rut: rutNormalizado || null,
            activo,
          })
          .eq('id', proveedor.id)
          .eq('empresa_id', empresaActual.id)
          .select('id, razon_social, rut, activo')
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        proveedorGuardado = data
      } else {
        const nuevoProveedor = {
          empresa_id: empresaActual.id,
          razon_social: nombreLimpio,
          rut: rutNormalizado || null,
          activo: true,
        }

        const { data, error: errorInsertar } = await supabase
          .from('proveedores')
          .insert(nuevoProveedor)
          .select('id, razon_social, rut, activo')
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        proveedorGuardado = data
      }

      onGuardado?.(proveedorGuardado)
    } catch (errorGuardado) {
      console.error('Error al guardar proveedor:', errorGuardado)

      if (errorGuardado.code === '23505') {
        setError(
          'Ya existe un proveedor con ese RUT en esta empresa.'
        )
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar el proveedor.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar proveedor' : 'Nuevo proveedor'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={guardarProveedor}
        >
          <div>
            <label
              htmlFor="razon_social"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Nombre del proveedor
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="razon_social"
              type="text"
              value={nombre}
              onChange={(event) => setNombre(event.target.value)}
              placeholder="Razón social del proveedor"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          <div>
            <label
              htmlFor="rut"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              RUT
            </label>

            <input
              id="rut"
              type="text"
              value={rut}
              onChange={(event) => setRut(event.target.value)}
              placeholder="12.345.678-9"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          {esEdicion && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) => setActivo(event.target.checked)}
                disabled={guardando}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Proveedor activo
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default ProveedorModal
